import React, { PureComponent } from 'react';
import styled, { withTheme } from 'styled-components';
import { Trans, withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { PrimaryHeader, SecondaryHeader } from '../../typography/Headers';
import LoginEmailForm from '../forms/LoginEmail';
import RegisterEmailForm from '../forms/RegisterEmail';
import { Paragraph, FormFieldError } from '../../typography/Content';
import { FacebookButton, LinkedinButton, AppleButton } from '../../buttons/Social';
import Buttons from '../../buttons';
import Link from '../../buttons/Link';
import Panel from '../../layout/Panel';
import DSGPropTypes from '@repo/ui/types/DSGPropTypes';

const Container = styled(Panel)`
  flex: ${(props) => (props.$create ? 3 : 2)};
  ${(props) => props.theme.media.desktop`flex: 3;`};
  transition: all 0.4s;
  text-align: center;
  background: ${(props) => props.theme.brandColors.lightestBackground};

  & > div {
    text-align: left;
  }
`;

Container._invertible = true;

const SocialOptions = styled.section`
  display: flex;
  flex-direction: column;
  & > button {
    margin-bottom: ${(props) => props.theme.margins.xs};
  }
`;

class AccountSection extends PureComponent {
  state = {
    loginValues: null,
    registerValues: null,
  };

  static contextTypes = {
    environment: PropTypes.object,
    functions: PropTypes.object,
  };

  static defaultProps = {
    showApple: false,
    onEmailLogin: () => true,
    onEmailRegister: () => true,
    onChangeRegister: () => true,
    onSocialConnect: (_method, next) => next(),
  };

  static propTypes = {
    /** Callback when the login form gets submitted (provides values/error handler) */
    onEmailLogin: PropTypes.func.isRequired,
    /** Callback when the registration form gets submitted (provides values/error handler) */
    onEmailRegister: PropTypes.func.isRequired,
    /** Callback when a social button begins to navigate, provides (method, callback) where callback must be executed to continue */
    onSocialConnect: PropTypes.func,
    /** Is the form in create account mode? */
    create: PropTypes.bool,
    /** Is there an invite token to track? */
    token: PropTypes.string,
    /** Router state to preserve when switching views */
    state: PropTypes.object,
    /** The location object where we should be navigating to after login */
    nextLocation: PropTypes.object,
    /** If the user has logged in before, we need to know the details */
    lastLogin: DSGPropTypes.lastLogin,
    /** If there was an error during Facebook/Linkedin connect, pass as string */
    socialError: PropTypes.string,
  };

  handleLoginChange = (values) => {
    this.setState({ loginValues: values });
  };

  handleRegisterChange = (values) => {
    this.setState({ registerValues: values });
    this.props.onChangeRegister(values);
  };

  handleLoginSubmit = (values, handleServerResponse) => {
    this.props.onEmailLogin(values, handleServerResponse);
  };

  handleRegisterSubmit = (values, handleServerResponse) => {
    this.props.onEmailRegister(values, handleServerResponse);
  };

  render() {
    const {
      api,
      create,
      theme,
      token,
      state,
      loading,
      nextLocation,
      nextLocationSocial,
      lastLogin,
      showApple,
      hidePasswordField,
      allowDisableEmailField,
      ssoNotice,
      enforceSso,
      socialError,
      t,
      i18n,
      onBack,
      onSocialConnect,
      onContinueSSO,
    } = this.props;
    const { environment, functions } = this.context;
    const { loginValues, registerValues } = this.state;

    return (
      <Container $create={create}>
        <PrimaryHeader>
          {create ? t('commonPlat:labels.createAccount') : t('commonPlat:actions.signIn')}
        </PrimaryHeader>
        <SecondaryHeader alternate>{t('commonPlat:labels.bySocial')}</SecondaryHeader>
        <SocialOptions>
          <FacebookButton
            disabled={enforceSso}
            nextLocation={nextLocationSocial || nextLocation}
            onNavigation={(next) => onSocialConnect('facebook', next)}
          />
          <LinkedinButton
            disabled={enforceSso}
            nextLocation={nextLocationSocial || nextLocation}
            onNavigation={(next) => onSocialConnect('linkedin', next)}
          />
          {showApple && (
            <AppleButton
              disabled={enforceSso}
              nextLocation={nextLocationSocial || nextLocation}
              onNavigation={(next) => onSocialConnect('apple', next)}
            />
          )}
          {hidePasswordField && (
            <Buttons.Alternate
              onClick={() => onSocialConnect('sso', nextLocationSocial || nextLocation)}
            >
              {t('commonPlat:actions.connectSSO')}
            </Buttons.Alternate>
          )}
          {socialError && <FormFieldError>{socialError}</FormFieldError>}
        </SocialOptions>
        <Paragraph>
          <Trans t={t} i18nKey="commonPlat:labels.socialAgreement">
            By connecting via social network, you agree to our{' '}
            <Link href={theme.urls.legal.terms(environment, functions, i18n)}>
              Terms of Service
            </Link>
            .
          </Trans>
        </Paragraph>
        <SecondaryHeader alternate>{t('commonPlat:labels.byEmail')}</SecondaryHeader>
        {!create && (
          <LoginEmailForm
            api={api}
            value={registerValues || { email: lastLogin && lastLogin.email }}
            token={token}
            state={state}
            loading={loading}
            hidePasswordField={hidePasswordField}
            allowDisableEmailField={allowDisableEmailField}
            onBack={onBack}
            onChange={this.handleLoginChange}
            onSubmit={this.handleLoginSubmit}
            autoFocus
          />
        )}
        {create && (
          <RegisterEmailForm
            api={api}
            value={loginValues}
            token={token}
            state={state}
            loading={loading}
            hidePasswordField={hidePasswordField}
            ssoNotice={ssoNotice}
            enforceSso={enforceSso}
            onContinueSSO={onContinueSSO}
            onChange={this.handleRegisterChange}
            onSubmit={this.handleRegisterSubmit}
            autoFocus
          />
        )}
      </Container>
    );
  }
}

export default withTheme(withTranslation(['commonPlat'])(AccountSection));
